import i18next from 'i18next';
import { resolveSupportedLanguage } from 'locales/languages';
import { type User } from 'oidc-client-ts';
import { create } from 'zustand';

import auth from '../auth';

import service from './service';
import { type AuthContext, type SessionState } from './types';

const allowedUserTypes = ['taxpayer', 'system'];
let currentHydrationToken: string | undefined;

const initialSession: SessionState = {
  status: 'loading',
  user: undefined,
  loading: true
};

const useAuthSessionStore = create<SessionState>(() => initialSession);

const changeLanguage = (value: string) => i18next.changeLanguage(value);

export function getAuthSession() {
  return useAuthSessionStore.getState();
}

export function useAuthSession() {
  return useAuthSessionStore();
}

export function useAuthUserContext() {
  return useAuthSessionStore((state) => state.user?.context);
}

export function setAuthLoading(loading: boolean) {
  useAuthSessionStore.setState((current) => ({
    ...current,
    loading,
    status: loading ? 'loading' : current.user ? 'authenticated' : 'anonymous'
  }));
}

export function beginLocalLogout() {
  useAuthSessionStore.setState((current) => {
    if (current.status === 'logging-out') return current;
    return { ...current, status: 'logging-out', loading: false };
  });
}

export function clearLocalAuthSession() {
  currentHydrationToken = undefined;
  useAuthSessionStore.setState({
    status: 'anonymous',
    user: undefined,
    loading: false
  });
}

// Kept as the coordinator's Task 3 dependency while callers migrate to the explicit local name.
export const clearAuthSession = clearLocalAuthSession;

export async function hydrateAuthSession(user: User) {
  const accessToken = user.access_token || '';
  const session = getAuthSession();

  if (currentHydrationToken === accessToken && session.user?.context) {
    return;
  }

  currentHydrationToken = accessToken;
  setAuthLoading(true);

  try {
    const context: AuthContext = await service.getContext();
    const userType = context?.userProfile?.userType;

    if (userType && !allowedUserTypes.includes(userType)) {
      void auth.logout();
      return;
    }

    useAuthSessionStore.setState({
      status: 'authenticated',
      user: {
        accessToken,
        idToken: user.id_token || '',
        context
      },
      loading: false
    });

    const language = resolveSupportedLanguage(context?.userProfile?.locale);
    if (language !== i18next.resolvedLanguage) {
      await changeLanguage(language);
    }
  } catch (error) {
    console.dir(error);
    currentHydrationToken = undefined;
    useAuthSessionStore.setState({
      status: 'error',
      user: undefined,
      loading: false
    });
  }
}
